import { useCallback, useEffect, useRef, useState } from 'react';
import { createUserLocalStorage, getUserLocalStorage } from './userLocalStorage';
import { trackAvatarCreationFromPopup, trackEnterToStudio, trackUserReturns } from './analyticsTracker';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_CODES, SubscriptionCode } from './types';

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export default usePrevious;

export const useScrollTo = (
	ref: React.RefObject<HTMLElement>,
	behavior: ScrollBehavior = "smooth",
	block: ScrollLogicalPosition = "start",
) => {
	const scrollTo = useCallback(() => {
		if (ref.current) {
			ref.current.scrollIntoView({
				behavior,
				block,
			});
		}
	}, [ref, behavior, block]);

	return scrollTo;
};

export const useLocalStorage = (key: string) => {
	const [isError, setIsError] = useState<boolean>(false);

	const setValue = (val: any) => {
		try {
			localStorage.setItem(key, JSON.stringify(val));
		} catch {
			setIsError(true);
		}
	};

	const getValue = (defaultValue: any) => {
		try {
			const item = localStorage.getItem(key);
			return item ? JSON.parse(item) : defaultValue;
		} catch {
			setIsError(true);
		}
	};

	const removeValue = () => {
		try {
			localStorage.removeItem(key);
		} catch {
			setIsError(true);
		}
	};

	useEffect(() => {
		setIsError(false);
	}, []);

	return { setValue, getValue, removeValue, isError };
};

export const useMetrikaLoaded = () => {
  const [isLoadedMetrika, setIsLoaded] = useState<boolean>(typeof window.ym === "function");

  useEffect(() => {
    const handler = () => setIsLoaded(true);
    window.addEventListener("metrika-loaded", handler);

    return () => {
      window.removeEventListener("metrika-loaded", handler);
    };
  }, []);

  return isLoadedMetrika;
};

export const useTrackUserWithMetrika = (isLoadedMetrika: boolean) => {
  useEffect(() => {
    const { userId, userFirstEnter } = getUserLocalStorage();
    const userCurrentEnter = new Date().toLocaleString();

    if (isLoadedMetrika) {
      trackEnterToStudio();
      if (userId && userFirstEnter) {
        trackUserReturns(userId, userFirstEnter, userCurrentEnter);
      } else {
        createUserLocalStorage();
      }
    }
  }, [isLoadedMetrika]);
};

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;

        const userAgent = navigator.userAgent;
        const maxTouchPoints = navigator.maxTouchPoints;
        const hasTouch = 'ontouchstart' in window;

        const isTouchDevice = hasTouch || maxTouchPoints > 0;
        const userAgentIsMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

        const isDevToolsEmulated =
            userAgentIsMobile &&
            width >= 768 &&
            height >= 600;
            
        const realMobile = userAgentIsMobile && isTouchDevice && !isDevToolsEmulated;

        setIsMobile(realMobile);
    };

    checkMobile();

    window.addEventListener("resize", checkMobile);
    window.addEventListener("orientationchange", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("orientationchange", checkMobile);
    };
  }, []);

  return { isMobile };
};

export const useVisperSearchParams = (lang: string | null, source: string | null, user: string | null, userSub: SubscriptionCode | null, searchParams: URLSearchParams, setSearchParams: (params: URLSearchParams) => void) => {
    const { i18n } = useTranslation();

    useEffect(() => {
        const params = new URLSearchParams(searchParams);

        if (lang) {
            params.set("lang", lang);
            i18n.changeLanguage(lang);
        }

        if (userSub) {
          if (userSub === SUBSCRIPTION_CODES.paid || userSub === SUBSCRIPTION_CODES.free) {
            localStorage.setItem("userSub", userSub);
          } else {
            localStorage.setItem("userSub", SUBSCRIPTION_CODES.free);
          }
          params.delete("sub");
        }

        if (source === "visperVideo") {
            localStorage.setItem("isUserFromPopup", "true");
            params.delete("source");
        }

        if (user) {
            localStorage.setItem("user", user);
            params.delete("user");
        }

        setSearchParams(params);
    }, [lang, source, user, searchParams, setSearchParams, i18n]);
};